import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

interface Workspace {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string | null;
  domain_auto_join: string | null;
}

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  loading: boolean;
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);

  // Save current workspace to profile
  const saveCurrentWorkspaceToProfile = useCallback(async (workspaceId: string) => {
    if (!user) return;
    
    try {
      await supabase
        .from('profiles')
        .update({ current_workspace_id: workspaceId })
        .eq('id', user.id);
    } catch (error) {
      console.error('Error saving current workspace to profile:', error);
    }
  }, [user]);

  // Wrapper to set current workspace and persist it
  const setCurrentWorkspace = useCallback((workspace: Workspace) => {
    setCurrentWorkspaceState(workspace);
    saveCurrentWorkspaceToProfile(workspace.id);
  }, [saveCurrentWorkspaceToProfile]);

  const refreshWorkspaces = async () => {
    if (!user) {
      setWorkspaces([]);
      setCurrentWorkspaceState(null);
      setLoading(false);
      return;
    }

    try {
      // Fetch workspaces and user profile in parallel
      const [workspacesResult, profileResult] = await Promise.all([
        supabase
          .from('workspace_members')
          .select(`
            workspace:workspaces (
              id,
              name,
              slug,
              logo_url,
              primary_color,
              domain_auto_join
            )
          `)
          .eq('user_id', user.id),
        supabase
          .from('profiles')
          .select('current_workspace_id')
          .eq('id', user.id)
          .single()
      ]);

      if (workspacesResult.error) throw workspacesResult.error;

      const workspaceList = workspacesResult.data
        ?.map((item: any) => item.workspace)
        .filter(Boolean) as Workspace[];
      
      setWorkspaces(workspaceList || []);
      
      // Set current workspace from profile or default to first
      const savedWorkspaceId = profileResult.data?.current_workspace_id;
      const savedWorkspace = workspaceList?.find(w => w.id === savedWorkspaceId);
      
      if (savedWorkspace) {
        setCurrentWorkspaceState(savedWorkspace);
      } else if (workspaceList?.length > 0) {
        // Default to first workspace and save it
        setCurrentWorkspaceState(workspaceList[0]);
        saveCurrentWorkspaceToProfile(workspaceList[0].id);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshWorkspaces();
  }, [user]);

  return (
    <WorkspaceContext.Provider 
      value={{ 
        workspaces, 
        currentWorkspace, 
        setCurrentWorkspace, 
        loading,
        refreshWorkspaces 
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
